import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { SignedIn, SignedOut, useClerk } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { anyApi } from "convex/server";
import Landing from "./Landing";
import { HouseModernIcon } from "../components/icons";
import { AddPropertyDialog } from "../components/AddPropertyDialog";
import { useIsAdmin } from "../lib/use-is-admin";

type MyMembership = {
  slug: string;
  name: string;
  address: string;
  role: "landlord" | "tenant";
};

// Handles the `/` route: shows the global Landing to signed-out users,
// auto-redirects single-property users to their dashboard, and renders
// a property picker for users with memberships on multiple properties.
export default function RootSwitch() {
  return (
    <>
      <SignedOut>
        <Landing />
      </SignedOut>
      <SignedIn>
        <SignedInRoot />
      </SignedIn>
    </>
  );
}

function SignedInRoot() {
  const memberships = useQuery(anyApi.memberships.listMine, {}) as
    | MyMembership[]
    | undefined;

  if (memberships === undefined) {
    return <CenteredMessage label="Loading…" />;
  }

  if (memberships.length === 0) {
    return <NoMembershipsState />;
  }

  if (memberships.length === 1) {
    return <Navigate to={`/${memberships[0]!.slug}/dashboard`} replace />;
  }

  return <PropertyPicker memberships={memberships} />;
}

function PropertyPicker({ memberships }: { memberships: MyMembership[] }) {
  const [addOpen, setAddOpen] = useState(false);
  const canAdd = useIsAdmin();
  return (
    <div className="min-h-screen bg-slate-50 px-6 py-16">
      <div className="max-w-xl mx-auto">
        <div className="text-center">
          <HouseModernIcon size={36} className="mx-auto text-slate-900" />
          <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900">
            Pick a property
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            You have access to {memberships.length} properties.
          </p>
        </div>
        <ul className="mt-8 grid gap-3">
          {memberships.map((m) => (
            <li key={m.slug}>
              <Link
                to={`/${m.slug}/dashboard`}
                className="group block rounded-lg border border-slate-200 bg-white px-5 py-4 shadow-sm hover:border-slate-300 hover:shadow transition"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500">
                      {m.name}
                    </p>
                    <p className="mt-0.5 text-sm text-slate-700">{m.address}</p>
                  </div>
                  <span className="text-xs font-medium text-slate-500 group-hover:text-slate-900 transition-colors">
                    {m.role === "landlord" ? "Landlord" : "Tenant"} →
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
        {canAdd && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
            >
              + Add another property
            </button>
          </div>
        )}
      </div>
      <AddPropertyDialog open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}

function NoMembershipsState() {
  const clerk = useClerk();
  const isAdmin = useIsAdmin();
  const [addOpen, setAddOpen] = useState(false);
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="max-w-md w-full text-center">
        <HouseModernIcon size={36} className="mx-auto text-slate-900" />
        <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900">
          No properties yet
        </h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          {isAdmin
            ? "You're signed in as a platform admin. Add the first property to get started."
            : "You're signed in, but you don't have access to any properties on maisons. Ask your landlord for an invite."}
        </p>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="mt-8 inline-flex items-center justify-center rounded-md bg-slate-900 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-slate-800 transition-colors"
          >
            Add a property
          </button>
        )}
        <div>
          <button
            type="button"
            onClick={() => clerk.signOut()}
            className={`text-sm text-slate-600 hover:text-slate-900 transition-colors ${
              isAdmin ? "mt-4" : "mt-8"
            }`}
          >
            Sign out
          </button>
        </div>
      </div>
      {isAdmin && (
        <AddPropertyDialog open={addOpen} onClose={() => setAddOpen(false)} />
      )}
    </div>
  );
}

function CenteredMessage({ label }: { label: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}
